/**
 * Contract for Beanz Manager (roaster admin) product updates made from the admin
 * catalogue. Every update becomes one Salesforce approval request per SKU.
 *
 * This module is deliberately free of runtime imports (type-only imports are
 * erased) so frontend packages can import the value lists directly without
 * pulling an AWS SDK or other infrastructure chain across the
 * frontend/backend boundary.
 */
import type { TermValue, WebStatus } from "../services/pim/types";
import type { ApprovalRequestResult, WebStatusUpdate } from "../services/salesforce/types";
import type { CatalogueRegionId } from "./admin-catalogue";
import type { IntensityPosition, IntensityZone } from "./intensity";

/** Salesforce `Character__c` picklist values. */
export const ADMIN_CHARACTER_VALUES = [
  "Extra Bright",
  "Extra Intense",
  "Extra Punchy",
  "Fruity Espresso",
  "Funky",
  "Naturally Sweet",
  "Unique & Surprising",
] as const;

export type AdminCharacter = (typeof ADMIN_CHARACTER_VALUES)[number];

/** Salesforce `Badges__c` picklist values. */
export const ADMIN_BADGE_VALUES = [
  "Cold Extracted Enable",
  "Award Winner",
  "Beanz Exclusive",
  "Christmas Coffee",
  "Crowd Pleaser",
  "Easter Coffee",
  "Everyday Drinker",
  "Featured Coffee",
  "Limited Release",
  "Most Popular",
  "New Arrival",
  "Seasonal Blend",
  "Selling Fast",
  "Super Special Coffee",
  "Low Caffeine",
] as const;

export type AdminBadge = (typeof ADMIN_BADGE_VALUES)[number];

export const isAdminCharacter = (value: unknown): value is AdminCharacter =>
  typeof value === "string" && (ADMIN_CHARACTER_VALUES as readonly string[]).includes(value);

export const isAdminBadge = (value: unknown): value is AdminBadge =>
  typeof value === "string" && (ADMIN_BADGE_VALUES as readonly string[]).includes(value);

/** Product-level fields a Beanz Manager can edit. Also the `fieldName` written to the approval request history. */
export const ADMIN_ATTRIBUTE_FIELDS = ["intensityZone", "intensityPosition", "character", "badges"] as const;

export type AdminAttributeField = (typeof ADMIN_ATTRIBUTE_FIELDS)[number];

export const isAdminAttributeField = (value: unknown): value is AdminAttributeField =>
  typeof value === "string" && (ADMIN_ATTRIBUTE_FIELDS as readonly string[]).includes(value);

/**
 * One attribute change. `oldValue` is what the catalogue currently shows (null or
 * empty when unset) and is only recorded in the history, never validated against
 * the picklists: PIM may hold a value that predates them.
 */
export type AdminAttributeChange =
  | { field: "intensityZone"; oldValue: IntensityZone | null; newValue: IntensityZone }
  | { field: "intensityPosition"; oldValue: IntensityPosition | null; newValue: IntensityPosition }
  | { field: "character"; oldValue: string | null; newValue: AdminCharacter }
  | { field: "badges"; oldValue: string[]; newValue: AdminBadge[] };

/** Per-SKU data an approval request needs, taken from the admin catalogue. */
export interface AdminSkuContext {
  itemNumber: string;
  /** PIM `WEB_TERM`: "Short" or "Long". */
  coffeeTerm: string;
}

/** The coffee terms PIM and Salesforce support; any other value can't be checked against the Short Term rules. */
export const COFFEE_TERMS = ["Short", "Long"] as const satisfies readonly TermValue[];

export const isCoffeeTerm = (value: unknown): value is TermValue =>
  typeof value === "string" && (COFFEE_TERMS as readonly string[]).includes(value);

/** Shared by every admin update: which product, of which roaster, in which storefront. */
export interface AdminProductContext {
  region: CatalogueRegionId;
  /** `{businessUnit}:{vendorNumber}`, as returned on the admin catalogue product. */
  vendorKey: string;
  productName: string;
  /** IANA zone of the manager's browser; resolves "today" for the effective date. */
  timeZone?: string;
}

/** Updates one product-level attribute on every listed SKU of the product. */
export interface AdminAttributeUpdateRequest extends AdminProductContext {
  change: AdminAttributeChange;
  skus: AdminSkuContext[];
}

/** Web statuses a Beanz Manager can move a SKU to. */
export const ADMIN_TARGET_WEB_STATUSES = [
  "Visible for Sale",
  "Out of Stock",
  "Visible but not for Sale",
] as const satisfies readonly WebStatus[];

export type AdminTargetWebStatus = (typeof ADMIN_TARGET_WEB_STATUSES)[number];

export const isAdminTargetWebStatus = (value: unknown): value is AdminTargetWebStatus =>
  typeof value === "string" && (ADMIN_TARGET_WEB_STATUSES as readonly string[]).includes(value);

/**
 * The Salesforce `webStatus` each PIM status corresponds to. Several statuses share one value ("Visible but not for
 * Sale" and "Not Visible" are both Unpublish), so a change is only real when these values differ.
 */
export const WEB_STATUS_UPDATE_BY_STATUS: Record<WebStatus, WebStatusUpdate> = {
  "Visible for Sale": "Publish",
  "Out of Stock": "OutOfStock",
  "Temporarily Out of Stock": "OutOfStock",
  "Visible but not for Sale": "Unpublish",
  "Not Visible": "Unpublish",
};

/** Every PIM web status, in the order the admin catalogue lists them. */
export const WEB_STATUSES = Object.keys(WEB_STATUS_UPDATE_BY_STATUS) as WebStatus[];

export const isWebStatus = (value: unknown): value is WebStatus =>
  typeof value === "string" && Object.hasOwn(WEB_STATUS_UPDATE_BY_STATUS, value);

/**
 * Whether a status change takes a Short Term SKU out of stock: the one change that sends weekly and total inventory,
 * both as 0. Every other change, and every attribute change, sends no inventory.
 */
export const isShortTermOutOfStock = (coffeeTerm: string, webStatus: AdminTargetWebStatus): boolean =>
  coffeeTerm === "Short" && WEB_STATUS_UPDATE_BY_STATUS[webStatus] === "OutOfStock";

/** Target statuses that actually change Salesforce's `webStatus` for a SKU currently in `current`. */
export const adminTargetWebStatusesFrom = (current: WebStatus | undefined): AdminTargetWebStatus[] =>
  ADMIN_TARGET_WEB_STATUSES.filter(
    (status) => current === undefined || WEB_STATUS_UPDATE_BY_STATUS[status] !== WEB_STATUS_UPDATE_BY_STATUS[current]
  );

/** Changes the web status of a single SKU in the request's region. */
export interface AdminStatusChangeRequest extends AdminProductContext {
  sku: AdminSkuContext;
  currentWebStatus: WebStatus;
  webStatus: AdminTargetWebStatus;
  /** `yyyy-MM-dd`, today or later in `timeZone`. */
  effectiveDate: string;
  comments: string;
}

/** A SKU whose approval request Salesforce did not create. */
export interface AdminProductUpdateFailure {
  itemNumber: string;
  message: string;
}

/**
 * An approval request for the SKU, the same shape the roaster flow returns; either way it exists, so it is never retried.
 * - `alreadyPending`: an identical request was already awaiting approval, so none was sent; this is that request.
 * - `recordLookupFailed`: Salesforce created the request but its record couldn't then be identified, so
 *   `recordId`/`recordName` are empty.
 */
export type AdminProductUpdateResult = ApprovalRequestResult & { alreadyPending?: true; recordLookupFailed?: true };

/**
 * Outcome of an admin update. Each SKU is a separate Salesforce request, so some
 * can succeed while others fail; the request only errors when none succeed.
 */
export interface AdminProductUpdateResponse {
  results: AdminProductUpdateResult[];
  failures: AdminProductUpdateFailure[];
}
